import {
  GradientStop,
  LinearGradient,
  RadialGradient,
  GradientValue
} from "./gradientTypes";

let stopCounter = 0;

const generateStopId = (): string => {
  stopCounter += 1;
  return `stop-${Date.now()}-${stopCounter}`;
};

export const createGradientStop = (
  color: string,
  position: number,
  opacity: number = 100
): GradientStop => ({
  id: generateStopId(),
  color,
  position,
  opacity
});

export const createLinearGradient = (
  angle: number = 90,
  stops: GradientStop[] = []
): LinearGradient => ({
  type: "linear",
  angle,
  stops: sortStops(stops)
});

export const createRadialGradient = (
  centerX: number = 50,
  centerY: number = 50,
  shape: "circle" | "ellipse" = "circle",
  stops: GradientStop[] = []
): RadialGradient => ({
  type: "radial",
  centerX,
  centerY,
  shape,
  stops: sortStops(stops)
});

export const sortStops = (stops: GradientStop[]): GradientStop[] => {
  if (!Array.isArray(stops)) return [];
  return [...stops].sort((a, b) => a.position - b.position);
};

export const addStop = (
  gradient: GradientValue,
  stop: GradientStop
): GradientValue => ({
  ...gradient,
  stops: sortStops([...gradient.stops, stop])
});

export const removeStop = (
  gradient: GradientValue,
  stopId: string
): GradientValue => ({
  ...gradient,
  stops: gradient.stops.filter(s => s.id !== stopId)
});

export const updateStop = (
  gradient: GradientValue,
  stopId: string,
  updates: Partial<Omit<GradientStop, "id">>
): GradientValue => ({
  ...gradient,
  stops: sortStops(
    gradient.stops.map(s => (s.id === stopId ? { ...s, ...updates } : s))
  )
});

const hexToRgba = (hex: string, opacity: number): string => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity / 100})`;
};

const stopToCss = (stop: GradientStop): string =>
  `${hexToRgba(stop.color, stop.opacity)} ${stop.position}%`;

export const gradientToCss = (gradient: GradientValue): string => {
  const sorted = sortStops(gradient.stops);

  if (sorted.length === 0) {
    return "transparent";
  }

  const first = sorted[0]!;
  if (sorted.length === 1) {
    return hexToRgba(first.color, first.opacity);
  }

  const stopsStr = sorted.map(stopToCss).join(", ");

  if (gradient.type === "linear") {
    return `linear-gradient(${gradient.angle}deg, ${stopsStr})`;
  }

  const { centerX, centerY, shape } = gradient;
  return `radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stopsStr})`;
};

export const gradientToBarCss = (gradient: GradientValue): string => {
  const sorted = sortStops(gradient.stops);

  if (sorted.length === 0) {
    return "transparent";
  }

  const first = sorted[0]!;
  if (sorted.length === 1) {
    return hexToRgba(first.color, first.opacity);
  }

  const stopsStr = sorted.map(stopToCss).join(", ");
  return `linear-gradient(90deg, ${stopsStr})`;
};

export const isSerializedGradient = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("gradient:");

export const isGradientValue = (value: unknown): value is GradientValue =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  "stops" in value &&
  Array.isArray((value as GradientValue).stops) &&
  ((value as GradientValue).type === "linear" ||
    (value as GradientValue).type === "radial");

const parseRgba = (
  rgbaStr: string
): { color: string; opacity: number } | null => {
  const match = rgbaStr.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/
  );
  if (!match) return null;
  const r = parseInt(match[1] ?? "0", 10);
  const g = parseInt(match[2] ?? "0", 10);
  const b = parseInt(match[3] ?? "0", 10);
  const a = match[4] !== undefined ? parseFloat(match[4]) : 1;
  const hex = `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  return { color: hex, opacity: Math.round(a * 100) };
};

const parseHex = (
  hexStr: string
): { color: string; opacity: number } | null => {
  const match = hexStr.match(/^#([0-9a-fA-F]{3,8})$/);
  if (!match || !match[1]) return null;
  const hex = match[1];
  if (hex.length === 3) {
    return {
      color: `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`,
      opacity: 100
    };
  }
  if (hex.length === 6) {
    return { color: `#${hex}`, opacity: 100 };
  }
  if (hex.length === 8) {
    const a = parseInt(hex.substring(6, 8), 16) / 255;
    return { color: `#${hex.substring(0, 6)}`, opacity: Math.round(a * 100) };
  }
  return null;
};

const parseColorValue = (
  colorStr: string
): { color: string; opacity: number } => {
  const trimmed = colorStr.trim();
  const rgba = parseRgba(trimmed);
  if (rgba) return rgba;
  const hex = parseHex(trimmed);
  if (hex) return hex;
  return { color: trimmed, opacity: 100 };
};

const parseStopsCss = (stopsStr: string): GradientStop[] => {
  const stops: GradientStop[] = [];
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const char of stopsStr) {
    if (char === "(") depth++;
    else if (char === ")") depth--;
    else if (char === "," && depth === 0) {
      parts.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  if (current.trim()) parts.push(current.trim());

  for (const part of parts) {
    const posMatch = part.match(/([\d.]+)%\s*$/);
    const position = posMatch ? parseFloat(posMatch[1] ?? "0") : 0;
    const colorPart = posMatch
      ? part
          .substring(0, part.lastIndexOf(posMatch[1] ?? ""))
          .trim()
          .replace(/%?\s*$/, "")
      : part.trim();
    const { color, opacity } = parseColorValue(colorPart);
    stops.push(createGradientStop(color, position, opacity));
  }

  return stops;
};

export const parseGradientCss = (cssString: string): GradientValue | null => {
  const trimmed = cssString.trim();

  const linearMatch = trimmed.match(
    /^linear-gradient\(\s*([\d.]+)deg\s*,\s*(.*)\s*\)$/
  );
  if (linearMatch) {
    const angle = parseFloat(linearMatch[1] ?? "0");
    const stops = parseStopsCss(linearMatch[2] ?? "");
    return createLinearGradient(angle, stops);
  }

  const radialMatch = trimmed.match(
    /^radial-gradient\(\s*(circle|ellipse)\s+at\s+([\d.]+)%\s+([\d.]+)%\s*,\s*(.*)\s*\)$/
  );
  if (radialMatch) {
    const shape = (radialMatch[1] ?? "circle") as "circle" | "ellipse";
    const centerX = parseFloat(radialMatch[2] ?? "50");
    const centerY = parseFloat(radialMatch[3] ?? "50");
    const stops = parseStopsCss(radialMatch[4] ?? "");
    return createRadialGradient(centerX, centerY, shape, stops);
  }

  return null;
};

export const getColorAtPosition = (
  gradient: GradientValue,
  position: number
): string => {
  const sorted = sortStops(gradient.stops);
  if (sorted.length === 0) return "#000000";

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last) return "#000000";
  if (sorted.length === 1) return first.color;

  if (position <= first.position) return first.color;
  if (position >= last.position) return last.color;

  let lower: GradientStop = first;
  let upper: GradientStop = last;
  for (const stop of sorted) {
    if (stop.position <= position) lower = stop;
    if (stop.position >= position && stop === lower) {
      return stop.color;
    }
    if (stop.position >= position) {
      upper = stop;
      break;
    }
  }

  const range = upper.position - lower.position;
  if (range === 0) return lower.color;
  const t = (position - lower.position) / range;

  const lc = lower.color.replace("#", "");
  const uc = upper.color.replace("#", "");
  const lr = parseInt(lc.substring(0, 2), 16);
  const lg = parseInt(lc.substring(2, 4), 16);
  const lb = parseInt(lc.substring(4, 6), 16);
  const ur = parseInt(uc.substring(0, 2), 16);
  const ug = parseInt(uc.substring(2, 4), 16);
  const ub = parseInt(uc.substring(4, 6), 16);

  const r = Math.round(lr + (ur - lr) * t);
  const g = Math.round(lg + (ug - lg) * t);
  const b = Math.round(lb + (ub - lb) * t);

  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};
